import React, { useState } from 'react';
import styled from 'styled-components';
import { FaCalendarAlt, FaTimes, FaExclamationCircle, FaCheckCircle, FaUser, FaPhone, FaPaw, FaCommentAlt } from 'react-icons/fa';
import Icon from './Icon';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 15px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  margin: 1rem;

  @media (max-width: 768px) {
    padding: 1rem;
    margin: 0.5rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 1.5rem;
  padding: 0.5rem;
  
  &:hover {
    color: #333;
  }
`;

const Title = styled.h2`
  color: #2c3e50;
  margin-bottom: 1rem;
  text-align: center;
  font-size: 1.25rem;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.label`
  color: #2c3e50;
  font-weight: 500;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Input = styled.input`
  padding: 0.5rem 0.5rem 0.5rem 2.2rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.9rem;
  width: 100%;

  @media (max-width: 768px) {
    padding: 0.4rem 0.4rem 0.4rem 2.2rem;
    font-size: 0.85rem;
  }
`;

const DateTimeInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.5rem 0.5rem 2.2rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-top: 0.25rem;
  background-color: white;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease;
  -webkit-appearance: none;
  appearance: none;

  @media (max-width: 768px) {
    padding: 0.4rem 0.4rem 0.4rem 2.2rem;
    font-size: 0.85rem;
  }

  &:focus {
    outline: none;
    border-color: #ff9d5c;
    box-shadow: 0 0 0 2px rgba(255, 157, 92, 0.2);
  }

  &:hover {
    border-color: #ff9d5c;
  }

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    filter: invert(0.5);
  }

  &::-webkit-datetime-edit-fields-wrapper {
    color: #333;
  }

  &::-webkit-datetime-edit {
    color: #333;
  }

  &:focus::-webkit-datetime-edit {
    color: #333;
  }

  &:invalid {
    box-shadow: none;
  }

  &[type="datetime-local"] {
    min-height: 36px;
  }

  @media (max-width: 768px) {
    &::-webkit-datetime-edit {
      padding: 0;
    }
    
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
`;

const TextArea = styled.textarea`
  padding: 0.5rem 0.5rem 0.5rem 2.2rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.9rem;
  resize: vertical;
  min-height: 80px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 0.4rem 0.4rem 0.4rem 2.2rem;
    font-size: 0.85rem;
    min-height: 60px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    margin-top: 0.75rem;
  }
`;

const Button = styled.button`
  flex: 1;
  padding: 0.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    padding: 0.4rem;
    font-size: 0.85rem;
  }
`;

const CancelButton = styled(Button)`
  background: #f8f9fa;
  color: #666;
  border: 1px solid #ddd;
  
  &:hover {
    background: #e9ecef;
  }
`;

const SubmitButton = styled(Button)`
  background: #25D366;
  color: white;
  border: none;
  
  &:hover {
    background: #128C7E;
  }
  
  &:disabled {
    background: #128C7E;
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  padding: 0.25rem;
  border-radius: 4px;
  background-color: #dc354510;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.2rem;
  }
`;

const InfoText = styled.small`
  color: #666;
  margin-top: 0.25rem;
  display: block;
  line-height: 1.4;
  font-size: 0.8rem;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const ThankYouModalOverlay = styled(ModalOverlay)`
  z-index: 1100;
`;

const ThankYouModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  width: 90%;
  max-width: 400px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

const ThankYouTitle = styled.h3`
  color: #2c3e50;
  margin: 0;
`;

const ThankYouMessage = styled.p`
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

const ThankYouButton = styled(Button)`
  background: #25D366;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  width: auto;
  
  &:hover {
    background: #128C7E;
  }
`;

const CheckIcon = styled.div`
  color: #25D366;
  font-size: 3rem;
  margin-bottom: 1rem;
`;

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AppointmentModal: React.FC<AppointmentModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    tutorName: '',
    tutorPhone: '',
    petName: '',
    appointmentDateTime: '',
    observations: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);

  const validateDateTime = (dateTimeStr: string) => {
    const dateTime = new Date(dateTimeStr);
    const hours = dateTime.getHours();
    const dayOfWeek = dateTime.getDay();

    // Verifica se é domingo
    if (dayOfWeek === 0) {
      setErrorMessage('Não realizamos atendimentos aos domingos.');
      return false;
    }

    // Verifica horários para dias de semana
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      if (hours < 8 || hours >= 18) {
        setErrorMessage('Durante a semana, atendemos das 8h às 18h.');
        return false;
      }
    }

    // Verifica horários para sábado
    if (dayOfWeek === 6) {
      if (hours < 8 || hours >= 12) {
        setErrorMessage('Aos sábados, atendemos das 8h às 12h.');
        return false;
      }
    }

    setErrorMessage('');
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'appointmentDateTime') {
      if (value) {
        if (!validateDateTime(value)) {
          return;
        }
      }
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Dispara conversão Google Ads
    if (window.gtag_report_conversion) window.gtag_report_conversion();

    try {
      // Formata a data para exibição mais amigável
      const formattedDate = formData.appointmentDateTime 
        ? new Date(formData.appointmentDateTime).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : '';

      // Formata a mensagem para o WhatsApp
      const whatsappMessage = `*Agendamento de Consulta*\n\n` +
        `*Nome do Tutor:* ${formData.tutorName}\n` +
        (formData.tutorPhone ? `*Telefone:* ${formData.tutorPhone}\n` : '') +
        (formData.petName ? `*Nome do Pet:* ${formData.petName}\n` : '') +
        `*Data e Hora Desejada:* ${formattedDate}\n` +
        (formData.observations ? `\n*Observações:* ${formData.observations}\n` : '') +
        `\nGostaria de confirmar se é possível agendar a consulta para esta data e horário.`;

      // Codifica a mensagem para URL
      const encodedMessage = encodeURIComponent(whatsappMessage);
      
      // Número do WhatsApp da clínica (sem espaços ou caracteres especiais)
      const whatsappNumber = '77999441369';
      
      // Cria o link do WhatsApp
      const whatsappUrl = `https://api.whatsapp.com/send?phone=55${whatsappNumber}&text=${encodedMessage}`;
      
      // Abre o WhatsApp em uma nova aba
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      
      // Mostra o modal de agradecimento
      setShowThankYou(true);
      
      // Limpa o formulário
      setFormData({
        tutorName: '',
        tutorPhone: '',
        petName: '',
        appointmentDateTime: '',
        observations: ''
      });
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      setErrorMessage('Ocorreu um erro ao tentar abrir o WhatsApp. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseAll = () => {
    setShowThankYou(false);
    onClose();
  };

  // Limpa o formulário e mensagens de erro quando o modal é fechado
  React.useEffect(() => {
    if (!isOpen) {
      setFormData({
        tutorName: '',
        tutorPhone: '',
        petName: '',
        appointmentDateTime: '',
        observations: ''
      });
      setErrorMessage('');
      setShowThankYou(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay>
        <ModalContent>
          <CloseButton onClick={onClose}>
            <Icon icon={FaTimes} size={20} color="currentColor" />
          </CloseButton>
          <Title>Agendar Consulta</Title>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Nome do Tutor</Label>
              <InputWrapper>
                <IconWrapper>
                  <Icon icon={FaUser} size={16} color="currentColor" />
                </IconWrapper>
                <Input
                  type="text"
                  name="tutorName"
                  value={formData.tutorName}
                  onChange={handleChange}
                  required
                  placeholder="Seu nome completo"
                />
              </InputWrapper>
            </FormGroup>
            
            <FormGroup>
              <Label>Telefone do Tutor (Opcional)</Label>
              <InputWrapper>
                <IconWrapper>
                  <Icon icon={FaPhone} size={16} color="currentColor" />
                </IconWrapper>
                <Input
                  type="tel"
                  name="tutorPhone"
                  value={formData.tutorPhone}
                  onChange={handleChange}
                  placeholder="(00) 00000-0000"
                />
              </InputWrapper>
            </FormGroup>
            
            <FormGroup>
              <Label>Nome do Pet (Opcional)</Label>
              <InputWrapper>
                <IconWrapper>
                  <Icon icon={FaPaw} size={16} color="currentColor" />
                </IconWrapper>
                <Input
                  type="text"
                  name="petName"
                  value={formData.petName}
                  onChange={handleChange}
                  placeholder="Nome do seu pet"
                />
              </InputWrapper>
            </FormGroup>
            
            <FormGroup>
              <Label>Data e Horário da Consulta</Label>
              <InputWrapper>
                <IconWrapper>
                  <Icon icon={FaCalendarAlt} size={16} color="currentColor" />
                </IconWrapper>
                <DateTimeInput
                  type="datetime-local"
                  name="appointmentDateTime"
                  value={formData.appointmentDateTime}
                  onChange={handleChange}
                  required
                  min={8}
                  max={18}
                  placeholder=" "
                  onInvalid={(e) => e.preventDefault()}
                />
              </InputWrapper>
              <InfoText>
                Horário de funcionamento:<br />
                • Segunda a Sexta: 8h às 18h<br />
                • Sábado: 8h às 12h<br />
                • Domingo: Não há atendimento
              </InfoText>
              {errorMessage && (
                <ErrorMessage>
                  <Icon icon={FaExclamationCircle} size={14} color="currentColor" />
                  {errorMessage}
                </ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Observações (Opcional)</Label>
              <InputWrapper>
                <IconWrapper>
                  <Icon icon={FaCommentAlt} size={16} color="currentColor" />
                </IconWrapper>
                <TextArea
                  name="observations"
                  value={formData.observations}
                  onChange={handleChange}
                  placeholder="Adicione informações importantes sobre a consulta"
                />
              </InputWrapper>
            </FormGroup>
            
            <ButtonGroup>
              <CancelButton type="button" onClick={onClose}>
                Cancelar
              </CancelButton>
              <SubmitButton type="submit" disabled={isSubmitting}>
                <Icon icon={FaCalendarAlt} size={18} color="white" />
                {isSubmitting ? 'Agendando...' : 'Agendar'}
              </SubmitButton>
            </ButtonGroup>
          </Form>
        </ModalContent>
      </ModalOverlay>

      {showThankYou && (
        <ThankYouModalOverlay>
          <ThankYouModalContent>
            <CheckIcon>
              <Icon icon={FaCheckCircle} size={48} color="currentColor" />
            </CheckIcon>
            <ThankYouTitle>Agradecemos seu interesse!</ThankYouTitle>
            <ThankYouMessage>
              Em breve entraremos em contato via WhatsApp para confirmar sua consulta.
              Fique atento às mensagens!
            </ThankYouMessage>
            <ThankYouButton onClick={handleCloseAll}>
              Entendi
            </ThankYouButton>
          </ThankYouModalContent>
        </ThankYouModalOverlay>
      )}
    </>
  );
};

export default AppointmentModal; 